/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {closeModal, closeSecondaryModal, openModal, openSecondaryModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import EditLeadStatusModal from 'components/leads/EditLeadStatusModal'
import {ModalSize} from 'types/enums/ModalSize'
import {LeadStatus} from 'types/enums/LeadStatus'
import {LeadCategory} from 'types/enums/LeadCategory'
import type {Lead} from 'types/entities/Lead'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import LeadsAction from 'store/leads/LeadsAction'
import {toast} from 'utils/toastUtils'
import {buildLeadCreatePayload} from 'utils/leads/useInlineLeadPillUpdate'
import {
    getSystemOnlyOpportunityStatusReason,
    isManuallySelectableOpportunityStatus,
} from 'utils/leads/leadsUtils'

type LeadAction = typeof LeadsAction | typeof OpportunitiesAction

interface UseEditLeadStatusChangeParams {
    selectedLead: Lead | null | undefined
    action: LeadAction
    isOpportunity: boolean
    isSecondary: boolean | undefined
    onSuccess: (() => void) | undefined
    /** Factory callback to avoid circular import — caller renders <EditLeadModal lead={lead} /> */
    renderOpportunityModal: (lead: Lead) => React.ReactNode
}

export const useEditLeadStatusChange = ({
    selectedLead,
    action,
    isOpportunity,
    isSecondary,
    onSuccess,
    renderOpportunityModal,
}: UseEditLeadStatusChangeParams) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()

    const applyStatusChange = useCallback(async (newStatus: LeadStatus) => {
        if (!selectedLead?.id) return
        const payload: Partial<Lead> = buildLeadCreatePayload(selectedLead, { status: newStatus })
        const updatedLead = await dispatch(action.fetchCreateOrUpdateLead(payload))

        if (updatedLead && onSuccess) {
            onSuccess()
        }

        if (newStatus === LeadStatus.OpportunityCreated && updatedLead) {
            const leadAsOpportunity = {...updatedLead, category: LeadCategory.Opportunity}
            await dispatch(OpportunitiesAction.fetchLeadById(leadAsOpportunity))

            if (isSecondary) {
                dispatch(closeSecondaryModal())
                dispatch(
                    openSecondaryModal(
                        renderOpportunityModal(leadAsOpportunity),
                        {
                            modalSize: ModalSize.RoundedFullscreen,
                            closeModal: () => {
                                dispatch(OpportunitiesAction.resetSelectedLead())
                                dispatch(closeSecondaryModal())
                            },
                        },
                        t('Edit Opportunity')
                    )
                )
            } else {
                dispatch(closeModal())
                dispatch(
                    openModal(
                        renderOpportunityModal(leadAsOpportunity),
                        {
                            modalSize: ModalSize.RoundedFullscreen,
                            closeModal: () => {
                                dispatch(OpportunitiesAction.resetSelectedLead())
                                dispatch(closeModal())
                            },
                        },
                        t('Edit Opportunity')
                    )
                )
            }
        }
    }, [dispatch, selectedLead, action, isSecondary, onSuccess, renderOpportunityModal, t])

    const handleStatusChange = useCallback((newStatus: LeadStatus) => {
        if (!selectedLead) return

        // Offerte / Zusage / Abgeschlossen — system-only (unless already current)
        if (
            isOpportunity &&
            !isManuallySelectableOpportunityStatus(newStatus, selectedLead.status)
        ) {
            toast.warning(getSystemOnlyOpportunityStatusReason(t, newStatus))
            return
        }

        if (newStatus === LeadStatus.OpportunityCreated && selectedLead.status !== LeadStatus.OpportunityCreated) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Create Opportunity')}
                        message={t('Are you sure you want to create an opportunity for this lead?')}
                        onConfirm={() => {
                            void applyStatusChange(newStatus)
                            dispatch(closeSecondaryModal())
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    {modalSize: ModalSize.Small},
                    t('Confirm')
                )
            )
            return
        }

        const isRejectionStatus = newStatus === LeadStatus.Rejection || newStatus === LeadStatus.Disqualified
        if (isRejectionStatus) {
            dispatch(
                openSecondaryModal(
                    <EditLeadStatusModal
                        lead={selectedLead}
                        rejectionStatus={newStatus}
                        isSecondary
                        onSuccess={() => {
                            if (onSuccess) onSuccess()
                            dispatch(closeSecondaryModal())
                        }}
                    />,
                    {modalSize: ModalSize.Small},
                    isOpportunity ? t('Rejection Details') : t('Disqualification Details')
                )
            )
            return
        }

        void applyStatusChange(newStatus)
    }, [dispatch, selectedLead, isOpportunity, onSuccess, applyStatusChange, t])

    return {handleStatusChange}
}
